import dayjs, { Dayjs } from 'dayjs';
import PropTypes from 'prop-types';
import qs from 'qs';
import { createContext, useEffect, useState, useMemo, useContext } from 'react';
import type { MeetingsDatedQuery } from '../generated/graphql';
import axios from 'axios';

export interface AppStateContextInterface {
  currentTime: Dayjs;
  appParams: AppParamsT;
  fourWaySplit: boolean;
  additionalWinPriceColumn: string;
  controllerState: string;
}

export function matchDarwinTimestamp(ts: Dayjs = dayjs()) {
  const darwinConvCurrent = ts.tz('Australia/Darwin');

  const adjusted = darwinConvCurrent.isBefore(
    dayjs().tz('Australia/Darwin').second(0).minute(0).hour(5).millisecond(0)
  )
    ? darwinConvCurrent.subtract(1, 'day')
    : darwinConvCurrent;

  return adjusted.second(0).minute(0).hour(5).millisecond(0);
}

export const AppStateContext = createContext<AppStateContextInterface>({
  currentTime: dayjs(),
  appParams: {
    computerName: '',
    screenNumber: 0,
  },
  fourWaySplit: false,
  additionalWinPriceColumn: '',
  controllerState: ''
});

interface Props {
  children: React.ReactNode;
}

type NTJRaceMeetingDetail = {
  meeting: Pick<
    Exclude<MeetingsDatedQuery['meetingsDated'][number], null | undefined>,
    'id' | 'track' | 'type'
  >;
};

export type NTJRace = NTJRaceMeetingDetail &
  NonNullable<
    Exclude<MeetingsDatedQuery['meetingsDated'][number], null | undefined>
  >['races'][number];

type AppParamsT = {
  computerName: string;
  screenNumber: number;
};

export function AppStateProvider({ children }: Props) {
  const [currentTime, setCurrentTime] = useState(dayjs());

  const [fourWaySplit, setFourWaySplit] = useState(false);
  const [additionalWinPriceColumn, setAdditionalWinPriceColumn] = useState('');

  const [appParams, setAppParams] = useState<AppParamsT>({
    computerName: '',
    screenNumber: 0,
  });

  const [controllerState, setControllerState] = useState('');

  useEffect(() => {
    const params = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

    if (params?.screenNumber && params?.computerName)
      setAppParams({
        computerName: params.computerName.toString(),
        screenNumber: Number(params.screenNumber),
      });
  }, []);

  useEffect(() => {
    if (appParams) {
      const c = new AbortController();
      (async () => {
        const data = await axios.post(
          `${process.env.REACT_APP_LFI_API}/srv.lfi.LFIService/GetControllerById`,
          {
            controllerId: appParams.computerName,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Key ${process.env.REACT_APP_LFI_KEY}`,
            },
            signal: c.signal,
          }
        );

        setFourWaySplit(!!data?.data?.controller.fourWaySplit);
        setAdditionalWinPriceColumn(data?.data?.controller.additionalWinPriceCol)
        setControllerState(data?.data?.controller.state)
      })();

      return () => c.abort();
    }
  }, [appParams]);

  useEffect(() => {
    function updateTime() {
      const now = dayjs();
      setCurrentTime(now);
    }

    const timer = window.setInterval(updateTime, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [currentTime]);

  const memorizedValues = useMemo(
    () => ({
      currentTime,
      appParams,
      fourWaySplit,
      additionalWinPriceColumn,
      controllerState
    }),
    [appParams, currentTime, fourWaySplit, additionalWinPriceColumn, controllerState]
  );

  return (
    <AppStateContext.Provider
      value={memorizedValues}
    >
      {children}
    </AppStateContext.Provider>
  );
}

AppStateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function useAppState() {
  return useContext(AppStateContext);
}
